"use client";

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { TemplateCalendarView } from "@/features/calendar/components/templates/TemplateCalendarView.tsx";
import PATHS from "@/routes/paths.ts";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import GenericLoader from "@/components/GenericLoader.tsx";

export default function TemplateEditView() {
  const { t } = useTranslation(["common", "calendar"]);
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  // Use the templates store
  const {
    currentTemplate,
    isLoadingTemplate,
    templateError,
    fetchTemplateDetails,
    resetTemplateState,
  } = useTemplatesStore();

  // Fetch template details when component mounts or templateId changes
  useEffect(() => {
    if (templateId) {
      fetchTemplateDetails(templateId);
    }

    // Clean up when component unmounts
    return () => {
      resetTemplateState();
    };
  }, [templateId]);

  // Render loading state
  if (isLoadingTemplate) {
    return <GenericLoader className={"fixed!"} />;
  }

  // Render error state
  if (templateError || !currentTemplate) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-foreground flex flex-col items-center justify-center rounded-lg p-8 text-center">
          <Icon
            className="text-muted-foreground mb-4"
            height={"4rem"}
            icon="tabler:alert-triangle"
            width={"4rem"}
          />
          <h2 className="text-accent-foreground mb-2 text-xl font-semibold">
            {t("common:errors.genericErrorTitle")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {templateError || t("calendar:templates.templateNotFound")}
          </p>
          <Button onClick={() => navigate(PATHS.TUTOR.SCHEDULE.TEMPLATES)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("calendar:templates.backToTemplates")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh_-_4rem)] w-full overflow-hidden">
      <title>{`${t("calendar:templates.title")} - Ximly`}</title>
      <div className="w-full ps-2 2xl:pe-4">
        {/* Template Calendar View */}

        <div className="bg-background h-[calc(100dvh_-_4rem)] overflow-hidden rounded-lg shadow-sm">
          <TemplateCalendarView
            config={{
              hourHeight: 80,
              headerHeight: 60,
              minHour: 0,
              maxHour: 24, // Explicitly set maxHour to ensure all hours are shown
            }}
            template={currentTemplate}
          />
        </div>
      </div>
    </div>
  );
}
